import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { TbxTooltip } from '@/common/components'

const classes = {
    formControl_large: {
        width: '280px',
    },
}

/**
 * Material selector for quote items.
 * Displays a dropdown to select the material type.
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {string} props.value - The currently selected material ID
 * @param {Array<{materialId: string, materialName: string}>} props.options - Available material options
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled
 * @param {boolean} [props.isPartFromLibrary=false] - Whether the part is from the part library
 * @param {boolean} [props.isMaterialUnavailable=false] - Whether the material is unavailable for library parts
 * @param {Function} props.onChange - Callback when selection changes
 * @returns {React.ReactElement} A row with label and material dropdown
 */
const MaterialSelector = ({
    disabled = false,
    isMaterialUnavailable = false,
    isPartFromLibrary = false,
    onChange,
    options,
    value,
}) => {
    const { t } = useTranslation()

    const getTooltipTitle = () => {
        if (!isPartFromLibrary) return ''
        if (isMaterialUnavailable) {
            return t('Material for Part Library Part is no longer available for selection')
        }
        return t('Cannot edit parts from library')
    }

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Typography data-testid="nqp-quote-item-material-label">{t('Material')}</Typography>
            <TbxTooltip
                title={getTooltipTitle()}
                arrow
            >
                <FormControl
                    color="primary"
                    margin="none"
                    size="small"
                    sx={classes.formControl_large}
                    variant="outlined"
                >
                    <Select
                        disabled={disabled}
                        id="select-itemMaterial"
                        inputProps={{ 'data-testid': 'nqp-quote-item-material' }}
                        labelId="select-itemMaterial-label"
                        SelectDisplayProps={{ style: { textAlign: 'left' } }}
                        value={value}
                        onChange={(e) => onChange(e)}
                    >
                        {options?.map((material) => (
                            <MenuItem
                                key={material.materialId}
                                value={material.materialId}
                            >
                                {material.materialName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </TbxTooltip>
        </Box>
    )
}

MaterialSelector.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    isMaterialUnavailable: PropTypes.bool,
    isPartFromLibrary: PropTypes.bool,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            materialId: PropTypes.string.isRequired,
            materialName: PropTypes.string.isRequired,
        })
    ),
}

export default memo(MaterialSelector)
